import { Component, ViewChild, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { Lab, Scan, ScanStatus } from '../../../model';
import { fallbackImage } from '../index';
import { ScanStatusIconComponent } from '../scan-status-icon/scan-status-icon.component';
import { PipesModule } from '../../pipes/pipes.module';
import { ModelState } from '../../../store/model/model.state';
import { euclideanDeltaC } from 'src/utils/color-tools';
import { LabThresholdGaugesComponent } from '../lab-threshold-gauges/lab-threshold-gauges.component';
import { JsonObjectPreviewComponent } from '../json-object-preview/json-object-preview.component';
import { AuthenticationState } from '../../../store/auth/auth.state';
import { ProductionBatchState } from '../../../store/batch/batch.state';
import { UserRole } from '../../../model';
import { OperatorReasonDialogComponent } from '../operator-reason-dialog/operator-reason-dialog.component';
import { reasonLabel } from '../../../model/operator-reason';
import { ScanTagsPopupComponent } from '../scan-tags-popup/scan-tags-popup.component';

@Component({
  selector: 'app-scan-gallery-card',
  templateUrl: './scan-gallery-card.component.html',
  styleUrls: ['./scan-gallery-card.component.scss'],
  imports: [
    CommonModule,
    NzCardModule,
    NzDropdownModule,
    NzMenuModule,
    NzIconModule,
    NzImageModule,
    NzTagModule,
    NzGridModule,
    DatePipe,
    DecimalPipe,
    PercentPipe,
    ScanStatusIconComponent,
    PipesModule,
    LabThresholdGaugesComponent,
    JsonObjectPreviewComponent,
    OperatorReasonDialogComponent,
    ScanTagsPopupComponent,
  ],
})
export class ScanGalleryCardComponent {
  private readonly modelState = inject(ModelState);
  private readonly authState = inject(AuthenticationState);
  private readonly batchState = inject(ProductionBatchState);

  readonly gaugesOpen    = signal(false);
  readonly scanJsonTitle = signal<string | null>(null);
  /** DEV-286: true while the (Admin only) "Scan #N — Tags" window is open. */
  readonly tagsOpen      = signal(false);
  readonly isAdmin       = computed(() => this.authState.hasAnyRole(UserRole.Admin));

  openScanJson(event: MouseEvent): void {
    event.stopPropagation(); // prevent cardClick from firing
    this.scanJsonTitle.set(`Scan #${this.scan().sequenceInBatch} — JSON`);
  }

  openScanTags(event: MouseEvent): void {
    event.stopPropagation(); // prevent cardClick from firing
    this.tagsOpen.set(true);
  }

  readonly scan = input.required<Scan>();
  readonly referenceLab = input<Lab | undefined>(undefined);
  readonly showStats = input<boolean>(false);

  readonly cardClick = output<void>();
  /** Emits only for non-Operator roles; Operator dispatches internally via the reason popup. */
  readonly statusChange = output<ScanStatus>();
  readonly hoverStart = output<void>();
  readonly hoverEnd = output<void>();

  readonly ScanStatus = ScanStatus;
  readonly fallbackImage = fallbackImage;

  /** Reference to the embedded reason-picker popup. */
  @ViewChild(OperatorReasonDialogComponent)
  private reasonDialog?: OperatorReasonDialogComponent;

  /** Status awaiting Operator reason confirmation. */
  private _pendingStatus: ScanStatus | null = null;

  /**
   * True when the scan's state was last set by a user — i.e. `scanStateSetByRole` is a non-empty value
   * other than `'Algo'` (the marker the algorithm writes). Null / undefined / empty / whitespace → false.
   */
  readonly modifiedByUser = computed((): boolean => {
    const role = this.scan().scanStateSetByRole?.trim();
    return !!role && role !== 'Algo';
  });

  /** AC-5: Localized label for the stored reason key; null when no reason recorded. */
  readonly currentReasonLabel = computed((): string | null =>
    reasonLabel(this.scan().operatorStatusChangeReason)
  );

  readonly inspectionModel = computed(() => {
    const id = this.scan().inspectionModelId;
    if (!id) return undefined;
    return this.modelState.all.find(m => m.id === id);
  });

  readonly dc = computed(() => euclideanDeltaC(this.referenceLab(), this.scan().predicted));

  readonly dEColor = computed((): string => {
    const model = this.inspectionModel();
    const dE = this.scan().dE;
    if (!model?.isUsingDeltaE || model.deThreshold == null || dE == null) return 'black';
    return dE > model.deThreshold ? 'red' : 'green';
  });

  readonly dCColor = computed((): string => {
    const model = this.inspectionModel();
    const dc = this.dc();
    if (!model?.isUsingDeltaE || model.dcThreshold == null || dc == null) return 'black';
    return dc > model.dcThreshold ? 'red' : 'green';
  });

  readonly labChannelColors = computed((): { l: string; a: string; b: string } => {
    const model = this.inspectionModel();
    const predicted = this.scan().predicted;
    const ref = this.referenceLab();
    const noModel = 'darkgray';

    if (!model || model.isUsingDeltaE || !predicted || !ref) {
      return { l: noModel, a: noModel, b: noModel };
    }

    const dl = predicted.l - ref.l;
    const da = predicted.a - ref.a;
    const db = predicted.b - ref.b;

    return {
      l: (dl > model.dlMaxThreshold || dl < model.dlMinThreshold) ? 'red' : 'green',
      a: (da > model.daMaxThreshold || da < model.daMinThreshold) ? 'red' : 'green',
      b: (db > model.dbMaxThreshold || db < model.dbMinThreshold) ? 'red' : 'green',
    };
  });

  /** Returns e.g. "[A,B]" when not using deltaE and predicted breaks those channel thresholds. */
  readonly brokenLabThresholds = computed((): string | null => {
    const model = this.inspectionModel();
    const predicted = this.scan().predicted;
    const ref = this.referenceLab();
    const scanId = this.scan().id;

    if (!model || model.isUsingDeltaE || !predicted || !ref) {
      console.log(`[brokenLabThresholds] #${this.scan().sequenceInBatch} scan=${scanId} skipped: model=${!!model} isUsingDeltaE=${model?.isUsingDeltaE} predicted=${!!predicted} ref=${!!ref}`);
      return null;
    }

    const dl = predicted.l - ref.l;
    const da = predicted.a - ref.a;
    const db = predicted.b - ref.b;

    const broken: string[] = [];
    if (dl > model.dlMaxThreshold || dl < model.dlMinThreshold) broken.push('L');
    if (da > model.daMaxThreshold || da < model.daMinThreshold) broken.push('A');
    if (db > model.dbMaxThreshold || db < model.dbMinThreshold) broken.push('B');

    const result = broken.length > 0 ? `[${broken.join(',')}]` : null;
    console.log(`[brokenLabThresholds] #${this.scan().sequenceInBatch} scan=${scanId} dl=${dl.toFixed(2)}(${model.dlMinThreshold}..${model.dlMaxThreshold}) da=${da.toFixed(2)}(${model.daMinThreshold}..${model.daMaxThreshold}) db=${db.toFixed(2)}(${model.dbMinThreshold}..${model.dbMaxThreshold}) broken=${JSON.stringify(broken)} result=${result}`);
    return result;
  });

  private hideTimer: unknown;

  showGauges() {
    clearTimeout(this.hideTimer as number);
    this.gaugesOpen.set(true);
  }

  hideGauges() {
    this.hideTimer = setTimeout(() => this.gaugesOpen.set(false), 300);
  }

  colorPicker(color: Lab | undefined): string {
    return `lab(${color?.l} ${color?.a} ${color?.b})`;
  }

  deltaLabString(p: number | undefined, r: number | undefined): string {
    if (!p || !r) return '';
    if (p - r >= 0) return '+' + (p - r).toFixed(1);
    return '-' + (r - p).toFixed(1);
  }

  /**
   * AC-2: Intercepts a status-change menu selection.
   * - Operator: stores the pending status and opens the reason popup.
   * - Non-Operator: emits statusChange directly (parent handles dispatch).
   */
  onMenuStatusChange(status: ScanStatus): void {
    if (this.authState.hasExactlyRole(UserRole.Operator)) {
      this._pendingStatus = status;
      this.reasonDialog?.open();
    } else {
      this.statusChange.emit(status);
    }
  }

  /**
   * AC-2 + AC-3: Operator confirmed a reason — dispatch updateStatus with reason key.
   */
  onReasonSelected(reasonKey: string): void {
    if (!this._pendingStatus) return;
    const status = this._pendingStatus;
    this._pendingStatus = null;
    this.batchState.updateStatus(this.scan(), status, reasonKey).subscribe({
      next: () => { /* SSE will push updated Scan back to the card */ },
      error: (err) => console.error('[scan-gallery-card] onReasonSelected: updateStatus failed', err)
    });
  }

  /** AC-2: Operator cancelled — clear pending status, no backend call. */
  onReasonCancelled(): void {
    this._pendingStatus = null;
  }
}
